import HeadlessUIPlugin from '@headlessui/tailwindcss'
import TWForms from '@tailwindcss/forms'

import plugin from 'tailwindcss/plugin'

import type { Config } from 'tailwindcss'

const spacing = {
  0: '0',
  0.5: '0.125em',
  1: '0.25em',
  '1/100': '1%',
  1.5: '0.375em',
  2: '0.5em',
  '2/1': '20%',
  '2/100': '2%',
  2.5: '0.625em',
  3: '0.75em',
  3.5: '0.875em',
  4: '1em',
  5: '1.25em',
  '5/100': '5%',
  6: '1.5em',
  7: '1.75em',
  8: '2em',
  9: '2.25em',
  10: '2.5em',
  '10/100': '10%',
  11: '2.75em',
  12: '3em',
  13: '3.25em',
  14: '3.5em',
  '15/10': '150%',
  16: '4em',
  20: '5em',
  '20/100': '20%',
  24: '6em',
  '25/100': '25%',
  28: '7em',
  '30/100': '30%',
  32: '8em',
  35: '8.75em',
  36: '9em',
  37: '9.25em',
  40: '10em',
  44: '11em',
  48: '12em',
  '50/100': '50%',
  52: '13em',
  56: '14em',
  60: '15em',
  64: '16em',

  70: '17.5em',
  '70/100': '70%',
  71: '17.75em',
  72: '18em',
  '75/100': '75%',
  76: '19em', // 76 * 4 = 304
  80: '20em', // 80 * 4 = 320
  '80/100': '80%',
  '90/100': '90%',

  '95/100': '95%',
  96: '24em',
  '98/100': '98%',

  '99/100': '99%',
  105: '26em',
  108: '27em',
  115: '28.75em',
  140: '32em',
  144: '36em',
  154: '38.5em',
  'col-0.5': '9.25em',
  'col-1': '19em',
  'col-1.5': '28.75em',
  'col-2': '38.5em',
  'col-2-gaped': '39.5em',
  'col-gap': '0.5em',
  inherit: 'inherit',
  'left-menu': '11em',
  'left-menu-mini': '3em',
  printPageWidth: 'var(--printPageWidth)',

  px: '0.0625em',
  'right-menu': '19em',
}

// We want each package to be responsible for its own content.
const config: Omit<Config, 'content'> = {
  // content: ['./src/**/*.{js,jsx,ts,tsx}', './config/**/*.{js,jsx,ts,tsx}'],
  corePlugins: {
    preflight: false,
  },
  darkMode: ['selector', ['[data-dark-mode="true"]', '[data-mode="dark"]']],
  plugins: [
    plugin(({ addVariant }) => {
      addVariant('hcm', ['[data-dark-mode="true"] &', '[data-mode="hc"] &'])
    }),
    HeadlessUIPlugin,
    TWForms,
  ],
  theme: {
    extend: {
      blur: {
        '2xl': '2.5em',
        '3xl': '4em',

        DEFAULT: '0.5em',
        inherit: 'inherit',
        lg: '1em',
        md: '0.75em',
        sm: '0.25em',
        xl: '1.5em',
      },
      borderRadius: {
        '2xl': '1em',
        '3xl': '1.5em',
        DEFAULT: '0.25em',

        full: '999em',
        inherit: 'inherit',
        lg: '0.5em',
        md: '0.375em',

        none: '0',
        sm: '0.125em',
        xl: '0.75em',
      },
      borderWidth: {
        0: '0',
        1: '0.0625em',

        2: '0.125em',
        4: '0.25em',
        6: '0.375em',
        8: '1em',
        DEFAULT: '1px',
        inherit: 'inherit',
      },
      boxShadow: {
        '2xl': '0 1.5625em 3.125em -0.75em var(--color-shadow25)',
        DEFAULT: '0 0.0625em 0.1875em 0 var(--color-shadow10), 0 0.0625em 0.125em -0.0625em var(--color-shadow10)',
        inherit: 'inherit',

        inner: 'inset 0 0.125em 0.25em 0 var(--color-shadow05)',

        lg: '0 0.625em 0.9375em -0.1875em var(--color-shadow10), 0 0.25em 0.375em -0.25em var(--color-shadow10)',
        md: '0 0.25em 0.375em -0.0625em var(--color-shadow10), 0 0.125em 0.25em -0.125em var(--color-shadow10)',
        none: 'none',
        sm: '0 0.0625em 0.125em 0 var(--color-shadow05)',
        xl: '0 1.25em 1.5625em -0.3125em var(--color-shadow10), 0 0.5em 0.625em -0.375em var(--color-shadow10)',
      },
      colors: {
        background: 'var(--color-background)',
        black: 'var(--color-black)',
        blue: 'var(--color-blue)',
        blueLight: 'var(--color-blueLight)',
        blueLightest: 'var(--color-blueLightest)',
        current: 'currentColor',
        green: 'var(--color-green)',
        greenLight: 'var(--color-greenLight)',
        greenLightest: 'var(--color-greenLightest)',
        grey: 'var(--color-grey)',
        greyDark: 'var(--color-greyDark)',
        greyDarkBlue: 'var(--color-greyDarkBlue)',
        greyDarkestBlue: 'var(--color-greyDarkestBlue)',
        greyLight: 'var(--color-greyLight)',
        greyLightest: 'var(--color-greyLightest)',
        greyLightMed: 'var(--color-greyLightMed)',
        inherit: 'inherit',
        'inherit-color': 'inherit',
        navy: 'var(--color-navy)',
        orange: 'var(--color-orange)',
        orangeLight: 'var(--color-orangeLight)',
        orangeLightest: 'var(--color-orangeLightest)',
        primary: 'var(--color-primaryColor)',
        red: 'var(--color-red)',
        redLight: 'var(--color-redLight)',
        redLightest: 'var(--color-redLightest)',
        secondary: 'var(--color-secondaryColor)',
        teal: 'var(--color-teal)',
        tealLight: 'var(--color-tealLight)',
        tealLightest: 'var(--color-tealLightest)',
        tertiary: 'var(--color-tertiaryColor)',
        text: 'var(--color-textColor)',
        transparent: 'transparent',
        white: 'var(--color-white)',
        yellow: 'var(--color-yellow)',
      },
      columns: {
        '2xl': '42em',
        '2xs': '18em',

        '3xl': '48em',
        '3xs': '16em',
        '4xl': '56em',
        '5xl': '64em',
        '6xl': '72em',
        '7xl': '80em',

        lg: '32em',
        md: '28em',
        sm: '24em',
        xl: '36em',
        xs: '20em',
      },
      dropShadow: {
        '2xl': '0 1.5625em 1.5625em var(--color-shadow25)',
        DEFAULT: ['0 0.0625em 0.125em var(--color-shadow10)', '0 0.0625em 0.0625em var(--color-shadow05)'],
        inherit: 'inherit',

        lg: ['0 0.625em 0.5em var(--color-shadow05)', '0 0.25em 0.1875em var(--color-shadow10)'],
        md: ['0 0.25em 0.1875em var(--color-shadow05)', '0 0.125em 0.125em var(--color-shadow05)'],
        none: '0 0 #0000',
        sm: '0 0.0625em 0.0625em var(--color-shadow05)',

        xl: ['0 1.25em 0.8125em var(--color-shadow05)', '0 0.5em 0.3125em var(--color-shadow10)'],
      },
      fontFamily: {
        inherit: 'inherit',
        'inherit-family': 'inherit',
      },
      fontSize: {
        '2xl': ['1.5em', { lineHeight: '1.3125em' }],
        '3xl': ['1.875em', { lineHeight: '1.3125em' }],

        '4xl': ['2.25em', { lineHeight: '1.3125em' }],
        '5xl': ['3em', { lineHeight: '1.3125em' }],
        '6xl': ['3.75em', { lineHeight: '1.3125em' }],
        '7xl': ['4.5em', { lineHeight: '1.3125em' }],
        '8xl': ['6em', { lineHeight: '1.3125em' }],

        '9xl': ['8em', { lineHeight: '1.3125em' }],
        base: ['1em', { lineHeight: '1.3125em' }],
        inherit: 'inherit',
        'inherit-size': 'inherit',
        lg: ['1.125em', { lineHeight: '1.3125em' }],
        sm: ['0.875em', { lineHeight: '1.3125em' }],
        xl: ['1.25em', { lineHeight: '1.3125em' }],
        xs: ['0.75em', { lineHeight: '1.3125em' }],
        xxs: ['0.5em', { lineHeight: '1.3125em' }],
      },
      fontWeight: {
        inherit: 'inherit',
        'inherit-weight': 'inherit',
      },
      letterSpacing: {
        inherit: 'inherit',
        normal: '0em',

        tight: '-0.025em',
        tighter: '-0.05em',
        wide: '0.025em',
        wider: '0.05em',
        widest: '0.1em',
      },
      lineHeight: {
        3: '.75em',

        4: '1em',
        5: '1.25em',
        6: '1.5em',
        7: '1.75em',
        8: '2em',
        9: '2.25em',
        10: '2.5em',
        inherit: 'inherit',
      },
      maxHeight: spacing,
      maxWidth: spacing,
      minHeight: spacing,
      minWidth: spacing,
      outlineOffset: {
        0: '0',
        1: '0.0625em',
        2: '0.125em',
        4: '0.25em',
        8: '0.5em',
      },
      outlineWidth: {
        0: '0',
        1: '0.0625em',
        2: '0.125em',
        4: '0.25em',
        8: '0.5em',
      },
      ringOffsetWidth: {
        0: '0',
        1: '0.0625em',
        2: '0.125em',
        4: '0.25em',
        8: '0.5em',
      },
      ringWidth: {
        0: '0px',
        1: '0.0625em',

        2: '0.125em',
        4: '0.25em',
        8: '0.5em',
        DEFAULT: '0.1875em',
        inherit: 'inherit',
      },
      screens: {
        320: '320px',
        375: '375px',
        424: '424px',
        480: '480px',
        600: '600px',
        620: '620px',
        640: '640px',
        768: '768px',
        800: '800px',
        1024: '1024px',
        1280: '1280px',
        1366: '1366px',
        1600: '1600px',
        1920: '1920px',
        2560: '2560px',
        3008: '3008px',
        3360: '3360px',
        3840: '3840px',
        'col-2-gaped': '39.5em',

        'cols-2-both-menus': '79.25em',
        'cols-2-left-menu': '50.5em',
        'cols-2-left-menu-mini': '42.5em',
        'cols-2-left-menu-mini-fixed': {
          max: '50.499em',
          min: '42.5em',
        },
        printPageWidth: 'var(--printPageWidth)',
      },
      spacing,
      textDecorationThickness: {
        0: '0px',
        1: '0.0625em',

        2: '0.125em',
        4: '0.25em',
        8: '0.5em',
        auto: 'auto',
        'from-font': 'from-font',
      },
      textUnderlineOffset: {
        0: '0px',

        1: '0.0625em',
        2: '0.125em',
        4: '0.25em',
        8: '0.5em',
        auto: 'auto',
      },
      transitionProperty: {
        height: 'height',
        width: 'width',
      },
      width: spacing,
      zIndex: {
        0: 0,
        1: 1,
        2: 2,
        3: 3,
        4: 4,
        5: 5,
        10: 10,
        20: 20,
        30: 30,
        40: 40,
        50: 50,

        auto: 'auto',
        inherit: 'inherit',
        max: 2000,
        modals: 1000,
        overModals: 1001,
      },
    },
  },
  variants: { extend: {} },
}

export default config
